import React, { useState, useEffect } from 'react';
import BasePage from '../components/BasePage';
import Fields from '../components/Fields';
import Image from '../components/Image';
import DocumentButton from '../components/Buttons/DocumentButton';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, MonitorCog, Triangle } from 'lucide-react';


const Coordonnees = (props) => {
    const navigate = useNavigate();
    const [updateCoords, setUpdateCoords] = useState(false);

    // Copie des identifiants Jercio dans le presse-papiers
    const [hoveredField, setHoveredField] = useState("");

    const handleCopyToClipboard = (text, fieldName) => {
        navigator.clipboard.writeText(text).then(() => {
            const fieldLabel = fieldName === 'Identifiant' ? "L'identifiant" : "Le mot de passe";
            toast.success(`${fieldLabel} a été copié avec succès.`);  
        })
        .catch((err) => {
            toast.error(`Erreur lors de la copie du ${fieldName}.`);  
        });
    };

    const fields = [
        {
            key: 'fonction_name',
            name: 'Fonctions',
            icon: <FontAwesomeIcon icon="fa-regular fa-user" />

        }, {
            key: 'mails',
            single_key: 'mail',
            name: 'Email',
            icon: <FontAwesomeIcon icon="fa-regular fa-envelope" />
        }, {
            key: 'phones',
            format: 'phone',
            single_key: 'phone',
            name: '',
            icon: {
                1: <FontAwesomeIcon icon="fa-solid fa-phone" />,
                2: <FontAwesomeIcon icon="fa-solid fa-mobile-screen-button" />,
            }
        }
    ]

    const fieldsEntreprise = [
        {
            key: 'siren',
            name: 'Code Siren',
            icon: <FontAwesomeIcon icon="fa-solid fa-barcode" />
        },
    ]

    const pjs = [
        {
            key: 'rib',
            name: 'RIB'
        }, {
            key: 'kbis',
            name: 'KBIS'
        }
    ]

    // const entreprise = [
    //     {
    //         key: 'adresse',
    //         name: 'adresse'
    //     }, {
    //         key: 'code_postal',
    //         name: 'code_postal'
    //     }
    // ]

    const displayName = (contact) => {
        if (contact.civilite == 'M') contact.civilite += '.'

        let data = contact.civilite + ' ' + contact.prenom + ' ' + contact.nom
        // on vérifie s'il y a une update
        let isUpdated = false
        if (updateCoords) {
            isUpdated = data != updateCoords.civilite + ' ' + updateCoords.prenom + ' ' + updateCoords.nom
        }

        return display(data, isUpdated ? updateCoords.civilite + ' ' + updateCoords.prenom + ' ' + updateCoords.nom : false)
    }

    const displayDate = (date) => {
        // on vérifie s'il y a une update
        let isUpdated = false
        if (updateCoords) {
            isUpdated = date != updateCoords.date_naissance;
        }

        if (!date) return false;

        return display(format(date, 'dd/MM/yyyy'), isUpdated ? format(updateCoords.date_naissance, 'dd/MM/yyyy') : false)
    }

    const displayAddress = (entreprise) => {
        let data = entreprise.adresse + ' ' + entreprise.code_postal + ' ' + entreprise.ville
        // on vérifie s'il y a une update
        let isUpdated = false
        if (updateCoords) {
            isUpdated = data != (updateCoords.adresse + ' ' + updateCoords.code_postal + ' ' + updateCoords.ville)
        }

        return display(data, isUpdated ? updateCoords.adresse + ' ' + updateCoords.code_postal + ' ' + updateCoords.ville : false)
    }

    const display = (data, updated = false) => {
        return (
            <>
                <div className={updated ? 'line-through' : ''}>{data}</div>
                {updated && (
                    <div className="text-base mb-2 -mt-1 text-morange">{updated}</div>
                )}
            </>
        )
    }

    const checkForCurrentUpdate = (e) => {
        if (updateCoords) {
            e.preventDefault();
            toast.info('Une demande de modification des données est en cours. Vous devez attendre qu\'elle ait été validée pour en redemander une nouvelle')
        }
    }


    const content = (data) => {
        // Utilisez la variable data récupérée dans BasePage ici
        // Par exemple, vous pouvez afficher les données ou effectuer toute autre logique avec elles
        if (!data) {
            return false;
        }

        if (!data.contact.nom) {
            toast.warning('Erreur lors de la récupération des coordonnées')
            setTimeout(() => {
                navigate('/dashboard')
            }, 100)
            return false;
        }

        if (data.update_coords) {
            if (data.update_coords.fonction_id) {
                let fonction = data.fonctions.find((f) => f.id == data.update_coords.fonction_id)
                if (fonction) {
                    data.update_coords.fonction_name = fonction.name
                }
            }
            setUpdateCoords(data.update_coords)
        }

        return (
            <div id="coordonnees" className="">
                {data ? (
                    <>
                        {updateCoords && (
                            <div className="p-4 mt-8 rounded-3xl bg-morange text-white flex flex-col sm:flex-row items-center text-center md:text-start gap-4 align-center">
                                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" className="fa-2x" />
                                Une demande de modification de vos données personnelles est en cours. Les données renseignées en orange sont celles dont vous avez demandé la modification.
                            </div>
                        )}

                        <div className="bloc-info my-3 mt-8 flex items-center">
                            <Link to="edit" className="rounded-full bg-mdark text-white p-5 flex" onClick={checkForCurrentUpdate}>
                                <FontAwesomeIcon icon="fa-solid fa-pen" />
                            </Link>
                            <div className="ml-4">
                                <div className="font-bold text-lg">
                                    {displayName(data.contact)}
                                </div>
                                <div>
                                    {displayDate(data.contact.date_naissance)}
                                </div>
                            </div>
                        </div>

                        {/* Si les données existent, affiche-les */}
                        <div id="coords" className="px-6">
                            <Fields fields={fields} data={data.contact} update={updateCoords} show_icons={true} />

                            {/* Affichage de l'addresse */}
                            <div className='divide-y divide-mgray text-sm'>
                                <div className="py-3 flex items-center gap-3 border-grey">
                                    <div className=" text-xl text-mlime w-6 text-center">
                                        <FontAwesomeIcon icon="fa-solid fa-location-dot" />
                                    </div>
                                    <div className=''>
                                        <div className="font-bold text-mdark text-[16px]">Adresse</div>
                                        <div className={`text-gray-900 order-1 text-sm`}> {displayAddress(data.entreprise[0])}</div>
                                        <div className="font-bold text-lg"></div>
                                    </div>
                                </div>

                                <Fields fields={fieldsEntreprise} data={data.entreprise[0]} update={updateCoords} show_icons={true} />
                            </div>
                        </div>

                        {/* Affichage des identifiants Jerico */}
                        <div className="bloc-info my-10 flex items-center flex-col md:flex-row">
                            <div className="rounded-full bg-mdark text-white p-5 flex text-base md:text-lg mb-2 md:m-0">
                                <MonitorCog />
                            </div>
                            <div className="ml-4 flex flex-col">
                                <h3 className="font-bold mb-4 ">Identifiants Back Office Jerico</h3>

                                {data.identifiant_jerico?.datas?.identifiantsClient?.length > 0 ? (
                                    <div className=" flex flex-col">
                                        <div className="flex pb-2">
                                            <p className="font-semibold">Identifiant :</p>
                                            <div className="relative ml-2">
                                                <span
                                                    className="hover:underline cursor-pointer"
                                                    onClick={() => handleCopyToClipboard(data.identifiant_jerico.datas.identifiantsClient[0].login, 'Identifiant')}
                                                    onMouseEnter={() => setHoveredField("Identifiant")}
                                                    onMouseLeave={() => setHoveredField("")}
                                                >
                                                    {data.identifiant_jerico.datas.identifiantsClient[0].login}
                                                </span>
                                                {hoveredField === "Identifiant" && (
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black text-white text-sm px-2 py-1 rounded">
                                                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-[-4px] w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                                                        Copier
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex items-center">
                                            <p className="font-semibold">Mot de passe :</p>
                                            <div className="relative ml-2">
                                                <span
                                                    className="hover:underline cursor-pointer"
                                                    onClick={() => handleCopyToClipboard(data.identifiant_jerico.datas.identifiantsClient[0].decrypted_password, 'Mot de passe')}
                                                    onMouseEnter={() => setHoveredField("Mot de passe")}
                                                    onMouseLeave={() => setHoveredField("")}
                                                >
                                                    {data.identifiant_jerico.datas.identifiantsClient[0].decrypted_password}
                                                </span>
                                                {hoveredField === "Mot de passe" && (
                                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black text-white text-sm px-2 py-1 rounded">
                                                        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-[-3px] w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                                                        Copier
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div>Identifiants non disponibles.</div>
                                )}
                            </div>
                        </div>

                        <div id="pjs">
                            <div className="grid md:grid-cols-2 gap-4">
                                {pjs.map((pj, index) => (
                                    <div key={index} className={pj.key}>
                                        <div className="text-center">
                                            {data.pjs ? (
                                                data.pjs.pj[pj.key].url ? (
                                                    <DocumentButton
                                                        filename={data.pjs.pj[pj.key].url}
                                                        file_type="signature"
                                                        buttonTitle={`Mon ${pj.name}`}
                                                        className="bg-mlime px-10 py-5 rounded-3xl max-w-lg duration-500 hover:bg-colorbutton"
                                                        icon={<Download className="text-white" />}
                                                        loader={{ size: '60%', firstColor: 'white', secondColor: '#eeeeee88' }}
                                                    />
                                                ) : (
                                                    <p>Aucune pièce jointe trouvée...</p>
                                                )
                                            ) : (
                                                <p className="font-bold text-mlime">Pas de {pj.name} renseigné</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                ) : (
                    // Sinon, affiche un message de chargement ou d'erreur
                    <p>Chargement en cours...</p>
                )}
            </div>

        );
    };

    return <BasePage content={content} title={props.title ?? null} />;

}

export default Coordonnees;